"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect } from "react";
import { Link } from "@/i18n/navigation";
import { Chart as ChartJS, BarElement, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler, type ChartOptions } from "chart.js";
import { Chart } from "react-chartjs-2";
import { cachedFetch } from "@/lib/fetch-cache";
import { CDN_BASE, fullCardUrl, imageUrl } from "@/lib/image-url";
import { useBetaPrefix } from "@/lib/api/prefix.client";
import IconSelect from "@/app/components/IconSelect";
import AscensionHeatmap, { type AscensionMatrix } from "@/app/components/AscensionHeatmap";
import EloTrajectory, { type EloPoint } from "@/app/components/EloTrajectory";
import CharacterTag from "@/app/components/CharacterTag";
import { textColor } from "@/lib/character-colors";
ChartJS.register(BarElement, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler);
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
const GRID = "rgba(140,140,150,0.14)";
const TICK = "#8b8b93";
const MODE_COLORS: Record<string, string> = {
  solo: "#e8b830",
  coop: "#3873a9",
  daily: "#23935b",
  custom: "#6b5b8a"
};
const REST_COLORS: Record<string, string> = {
  SMITH: "#e8b830",
  HEAL: "#23935b",
  MEND: "#3aa8a0",
  DIG: "#c5894a",
  CLONE: "#6b5b8a",
  COOK: "#f07c1e",
  LIFT: "#d53b27",
  HATCH: "#3873a9",
  KINDLE: "#bf5a85"
};
interface ComparableRow {
  id: string;
  name?: string;
  label?: string;
  count: number;
  pct: number;
  community_pct?: number | null;
  pct_low_hp?: number;
  community_pct_low_hp?: number | null;
}
interface BoonRow {
  id: string;
  name: string;
  count: number;
  take_rate?: number;
  offered?: number;
  community_take_rate?: number | null;
}
interface CardDelta {
  id: string;
  your_pick_rate: number;
  community_pick_rate: number;
  gap: number;
  offered: number;
  picked: number;
}
interface RelicDelta {
  id: string;
  your_rate: number;
  community_rate: number;
  gap: number;
  runs_with: number;
  runs: number;
}
interface EventDivergence {
  event_id: string;
  event_name: string | null;
  option_id: string;
  option_label: string | null;
  your_pct: number;
  community_pct: number;
  gap: number;
  visits: number;
}
interface DangerCell {
  visits: number;
  avg_dmg_pct: number;
  death_rate: number;
  community_death_rate?: number | null;
}
interface ActivityWeek {
  week: string;
  runs: number;
  wins: number;
  win_rate: number;
  solo: number;
  coop: number;
  daily: number;
  custom: number;
}
interface CharacterRow {
  id: string;
  name?: string;
  runs: number;
  wins: number;
  win_rate: number;
  share: number;
  community_win_rate?: number | null;
  community_share?: number | null;
}
export interface PersonalBest {
  run_hash: string;
  character: string;
  run_time: number;
  ascension: number;
  floors_reached: number;
}
export interface PersonalBests {
  fastest_solo?: PersonalBest;
  fastest_multi?: PersonalBest;
  highest_ascension?: PersonalBest;
  todays_daily?: PersonalBest;
  fastest_daily?: PersonalBest;
}
export interface Insights {
  ascension_matrix?: AscensionMatrix;
  total_runs: number;
  total_wins?: number;
  total_losses?: number;
  win_rate?: number;
  by_character?: CharacterRow[];
  runs_walked: number;
  runs_capped?: boolean;
  deaths?: {
    encounters?: ComparableRow[];
    events?: ComparableRow[];
  };
  rest_sites?: ComparableRow[];
  ancient_picks?: BoonRow[];
  event_divergence?: EventDivergence[];
  card_picks?: {
    over_picked: CardDelta[];
    under_picked: CardDelta[];
  };
  relic_picks?: {
    over_carried: RelicDelta[];
    under_carried: RelicDelta[];
  };
  map_danger?: {
    act: number;
    types: Record<string, DangerCell>;
  }[];
  streaks?: {
    current_win_streak: number;
    best_win_streak: number;
  };
  elo?: {
    current: number;
    peak: number;
    lifetime: number;
    runs: number;
    by_character?: Record<string, {
      elo: number;
      runs: number;
      wins: number;
    }>;
    history: EloPoint[];
  } | null;
  activity?: ActivityWeek[];
  percentiles?: {
    win_rate: number;
    win_rate_percentile: number;
    runs: number;
    runs_percentile: number;
    players: number;
  } | null;
  records?: {
    fastest_win?: {
      run_time: number;
      run_hash: string;
    } | null;
    longest_run?: {
      run_time: number;
      run_hash: string;
    } | null;
    biggest_deck?: {
      size: number;
      run_hash: string;
    } | null;
  };
}
export interface EntityInfo {
  id: string;
  name: string;
  image_url: string | null;
  color?: string | null;
  pool?: string | null;
}
const CHARACTER_HEX: Record<string, string> = {
  ironclad: "#d53b27",
  silent: "#23935b",
  defect: "#3873a9",
  necrobinder: "#bf5a85",
  regent: "#f07c1e"
};
const CHARACTERS = ["ironclad", "silent", "defect", "necrobinder", "regent"] as const;
// Same canonical order the stats page uses, so both lists line up.
const CHAR_ORDER = ["IRONCLAD", "SILENT", "DEFECT", "NECROBINDER", "REGENT"];

// Insight scope filters: character (icon dropdown), ascension, players, and
// game version, all defaulting to All. Each change re-fetches the whole
// insights payload filtered to that slice; the version list mirrors the
// stats pages (the snapshot's stat_versions, newest first).
export interface InsightFilters {
  character: string | null;
  ascension: string;
  players: string;
  version: string;
}
export const EMPTY_INSIGHT_FILTERS: InsightFilters = {
  character: null,
  ascension: "",
  players: "",
  version: ""
};
export function insightFilterQuery(f: InsightFilters): string {
  const params = new URLSearchParams();
  if (f.character) params.set("character", f.character);
  if (f.ascension) params.set("ascension", f.ascension);
  if (f.players) params.set("players", f.players);
  if (f.version) params.set("version", f.version);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}
export function InsightsFilterBar(t0) {
  const $ = _c(66);
  const {
    value,
    onChange
  } = t0;
  const t = useT();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [versions, setVersions] = useState(t1);
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      fetch(`${API}/api/runs/versions`).then(_temp).then(d => setVersions(d?.stat_versions || [])).catch(_temp2);
    };
    t3 = [];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] !== onChange || $[4] !== value) {
    t4 = patch => onChange({
      ...value,
      ...patch
    });
    $[3] = onChange;
    $[4] = value;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const set = t4;
  let t5;
  if ($[6] !== t) {
    t5 = t("Any Character");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = value.character ?? "";
  let t7;
  if ($[8] !== t) {
    t7 = CHARACTERS.map(c => ({
      label: t(c.charAt(0).toUpperCase() + c.slice(1)),
      value: c.toUpperCase(),
      icon: `${CDN_BASE}/ui/characters/character_icon_${c}.webp`
    }));
    $[8] = t;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== set) {
    t8 = v => set({
      character: v || null
    });
    $[10] = set;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== t5 || $[13] !== t6 || $[14] !== t7 || $[15] !== t8) {
    t9 = <IconSelect label={t5} value={t6} options={t7} onChange={t8} />;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t) {
    t10 = t("Any Ascension");
    $[17] = t;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const t11 = value.ascension;
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = Array.from({
      length: 11
    }, _temp3);
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== set) {
    t13 = v_0 => set({
      ascension: v_0
    });
    $[20] = set;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== t10 || $[23] !== t13 || $[24] !== value.ascension) {
    t14 = <IconSelect label={t10} value={t11} options={t12} onChange={t13} />;
    $[22] = t10;
    $[23] = t13;
    $[24] = value.ascension;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t) {
    t15 = t("All");
    $[26] = t;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  const t16 = value.players;
  let t17;
  if ($[28] !== t) {
    t17 = t("Solo");
    $[28] = t;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t17) {
    t18 = {
      label: t17,
      value: "1"
    };
    $[30] = t17;
    $[31] = t18;
  } else {
    t18 = $[31];
  }
  let t19;
  if ($[32] !== t) {
    t19 = t("2P");
    $[32] = t;
    $[33] = t19;
  } else {
    t19 = $[33];
  }
  let t20;
  if ($[34] !== t19) {
    t20 = {
      label: t19,
      value: "2"
    };
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t) {
    t21 = t("3P");
    $[36] = t;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] !== t21) {
    t22 = {
      label: t21,
      value: "3"
    };
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== t) {
    t23 = t("4P");
    $[40] = t;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t23) {
    t24 = {
      label: t23,
      value: "4"
    };
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t18 || $[45] !== t20 || $[46] !== t22 || $[47] !== t24) {
    t25 = [t18, t20, t22, t24];
    $[44] = t18;
    $[45] = t20;
    $[46] = t22;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  let t26;
  if ($[49] !== set) {
    t26 = v_1 => set({
      players: v_1
    });
    $[49] = set;
    $[50] = t26;
  } else {
    t26 = $[50];
  }
  let t27;
  if ($[51] !== t15 || $[52] !== t25 || $[53] !== t26 || $[54] !== value.players) {
    t27 = <IconSelect label={t15} value={t16} options={t25} onChange={t26} />;
    $[51] = t15;
    $[52] = t25;
    $[53] = t26;
    $[54] = value.players;
    $[55] = t27;
  } else {
    t27 = $[55];
  }
  let t28;
  if ($[56] !== set || $[57] !== t || $[58] !== value.version || $[59] !== versions) {
    t28 = versions.length > 0 && <IconSelect label={t("Any Version")} value={value.version} options={versions.map(_temp4)} onChange={v_3 => set({
      version: v_3
    })} />;
    $[56] = set;
    $[57] = t;
    $[58] = value.version;
    $[59] = versions;
    $[60] = t28;
  } else {
    t28 = $[60];
  }
  let t29;
  if ($[61] !== t14 || $[62] !== t27 || $[63] !== t28 || $[64] !== t9) {
    t29 = <div className="flex flex-wrap items-center gap-2">{t9}{t14}{t27}{t28}</div>;
    $[61] = t14;
    $[62] = t27;
    $[63] = t28;
    $[64] = t9;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  return t29;
}

// Card-pool pill: the character whose pool the card belongs to, so players
// grinding several characters can tell which one a pick-delta is about.
function _temp4(v_2) {
  return {
    label: v_2,
    value: v_2
  };
}
function _temp3(_, i) {
  return {
    label: `A${i}`,
    value: String(i)
  };
}
function _temp2() {}
function _temp(r) {
  return r.ok ? r.json() : null;
}
function CharacterPill(t0) {
  const $ = _c(21);
  const {
    color
  } = t0;
  const t = useT();
  const t1 = color || "";
  let hex;
  let key;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== t || $[1] !== t1) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      key = t1.toLowerCase();
      hex = CHARACTER_HEX[key];
      if (!hex) {
        t5 = null;
        break bb0;
      }
      t4 = t;
      t2 = key.charAt(0).toUpperCase();
      t3 = key.slice(1);
    }
    $[0] = t;
    $[1] = t1;
    $[2] = hex;
    $[3] = key;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    hex = $[2];
    key = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  const t6 = t2 + t3;
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = t4(t6);
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const label = t7;
  const t8 = `${hex}26`;
  let t9;
  if ($[11] !== hex || $[12] !== t8) {
    t9 = {
      borderColor: hex,
      backgroundColor: t8
    };
    $[11] = hex;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  const t10 = `${CDN_BASE}/ui/characters/character_icon_${key}.webp`;
  let t11;
  if ($[14] !== label || $[15] !== t10) {
    t11 = <img src={t10} alt={label} crossOrigin="anonymous" loading="lazy" className="w-3.5 h-3.5 object-contain" />;
    $[14] = label;
    $[15] = t10;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== label || $[18] !== t11 || $[19] !== t9) {
    t12 = <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border shrink-0" style={t9} title={label}>{t11}</span>;
    $[17] = label;
    $[18] = t11;
    $[19] = t9;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  return t12;
}
const DANGER_TYPES = ["monster", "elite", "boss", "unknown"] as const;
const DANGER_LABELS: Record<string, string> = {
  monster: "Monsters",
  elite: "Elites",
  boss: "Bosses",
  unknown: "Unknown rooms"
};
function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor(seconds % 3600 / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}
function Section(t0) {
  const $ = _c(5);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-3">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className="bg-[var(--bg-card)] rounded-lg border border-[var(--border-subtle)] p-4">{t1}{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

// The "you vs everyone" primitive: two thin bars on one scale.
function CompareBars(t0) {
  const $ = _c(27);
  const {
    you,
    community
  } = t0;
  const t = useT();
  let t1;
  if ($[0] !== t) {
    t1 = t("You");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="w-16 text-[10px] text-[var(--text-tertiary)]">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = `${Math.min(you, 100)}%`;
  let t4;
  if ($[4] !== t3) {
    t4 = <div className="flex-1 h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden"><div className="h-full rounded-full bg-[var(--accent-gold)]" style={{
        width: t3
      }} /></div>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== you) {
    t5 = <span className="w-12 text-right text-[10px] tabular-nums text-[var(--text-primary)]">{you}%</span>;
    $[6] = you;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t2 || $[9] !== t4 || $[10] !== t5) {
    t6 = <div className="flex items-center gap-2">{t2}{t4}{t5}</div>;
    $[8] = t2;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t) {
    t7 = t("Community");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <span className="w-16 text-[10px] text-[var(--text-tertiary)]">{t7}</span>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const t9 = `${Math.min(community ?? 0, 100)}%`;
  let t10;
  if ($[16] !== t9) {
    t10 = <div className="flex-1 h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden"><div className="h-full rounded-full bg-[var(--accent-gold)] opacity-40" style={{
        width: t9
      }} /></div>;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = community != null ? `${community}%` : "\u2014";
  let t12;
  if ($[18] !== t11) {
    t12 = <span className="w-12 text-right text-[10px] tabular-nums text-[var(--text-tertiary)]">{t11}</span>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== t10 || $[21] !== t12 || $[22] !== t8) {
    t13 = <div className="flex items-center gap-2">{t8}{t10}{t12}</div>;
    $[20] = t10;
    $[21] = t12;
    $[22] = t8;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t13 || $[25] !== t6) {
    t14 = <div className="space-y-0.5">{t6}{t13}</div>;
    $[24] = t13;
    $[25] = t6;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  return t14;
}

// Savant-style percentile slider: a track with a positioned, color-coded dot.
function PercentileSlider(t0) {
  const $ = _c(33);
  const {
    label,
    valueText,
    percentile
  } = t0;
  const t = useT();
  let t1;
  if ($[0] !== percentile) {
    t1 = Math.round(percentile / 100 * 120);
    $[0] = percentile;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hue = t1;
  const color = `hsl(${hue}, 65%, 52%)`;
  let t2;
  if ($[2] !== label) {
    t2 = <span className="text-[var(--text-secondary)]">{label}</span>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== valueText) {
    t3 = <span className="text-[var(--text-primary)] font-medium tabular-nums">{valueText}</span>;
    $[4] = valueText;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="flex items-center justify-between text-sm">{t2}{t3}</div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = `${percentile}%`;
  let t6;
  if ($[9] !== color || $[10] !== t5) {
    t6 = <div className="absolute inset-y-0 left-0 rounded-full opacity-25" style={{
      width: t5,
      backgroundColor: color
    }} />;
    $[9] = color;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const t7 = `${Math.min(Math.max(percentile, 3), 97)}%`;
  let t8;
  if ($[12] !== color || $[13] !== t7) {
    t8 = {
      left: t7,
      backgroundColor: color
    };
    $[12] = color;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== percentile || $[16] !== t8) {
    t9 = <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2 border-[var(--bg-card)] flex items-center justify-center text-[8px] font-bold text-on-fill" style={t8}>{percentile}</div>;
    $[15] = percentile;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t6 || $[19] !== t9) {
    t10 = <div className="relative h-2 rounded-full bg-[var(--bg-primary)]">{t6}{t9}</div>;
    $[18] = t6;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== t) {
    t11 = t("Better than");
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t) {
    t12 = t("of ranked players");
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== percentile || $[26] !== t11 || $[27] !== t12) {
    t13 = <p className="text-[10px] text-[var(--text-muted)]">{t11} {percentile}% {t12}</p>;
    $[25] = percentile;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13 || $[31] !== t4) {
    t14 = <div className="space-y-1">{t4}{t10}{t13}</div>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t4;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  return t14;
}
function GapBadge(t0) {
  const $ = _c(6);
  const {
    gap
  } = t0;
  const cls = gap > 0 ? "text-success" : gap < 0 ? "text-danger" : "text-[var(--text-muted)]";
  const t1 = `text-xs font-medium tabular-nums ${cls}`;
  const t2 = gap > 0 ? "+" : "";
  let t3;
  if ($[0] !== gap) {
    t3 = gap.toFixed(1);
    $[0] = gap;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t1 || $[3] !== t2 || $[4] !== t3) {
    t4 = <span className={t1}>{t2}{t3}%</span>;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function CardDeltaList(t0) {
  const $ = _c(17);
  const {
    rows,
    cards,
    lang
  } = t0;
  const t = useT();
  const bp = useBetaPrefix();
  if (rows.length === 0) {
    let t1;
    if ($[0] !== t) {
      t1 = t("Not enough data yet.");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <p className="text-xs text-[var(--text-muted)]">{t1}</p>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== bp || $[5] !== cards || $[6] !== lang || $[7] !== rows || $[8] !== t) {
    let t2;
    if ($[10] !== bp || $[11] !== cards || $[12] !== lang || $[13] !== t) {
      t2 = d => {
        const info = cards[d.id];
        return <Link prefetch={false} key={d.id} href={`${bp}/cards/${d.id.toLowerCase()}`} className="flex items-center gap-3 py-1.5 hover:bg-[var(--bg-card-hover)] rounded px-2 -mx-2 transition-colors"><span className="flex-shrink-0 w-10 h-[52px] flex items-center justify-center"><img src={fullCardUrl(d.id.toLowerCase(), false, "stable", lang)} alt={info?.name || d.id} crossOrigin="anonymous" loading="lazy" className="max-w-full max-h-full object-contain drop-shadow" onError={e => {
              const el = e.currentTarget;
              if (info?.image_url && !el.dataset.fellBack) {
                el.dataset.fellBack = "1";
                el.src = imageUrl(info.image_url);
              }
            }} /></span><span className="flex-1 min-w-0"><span className="flex items-center gap-1.5 min-w-0"><span className="truncate text-sm text-[var(--text-primary)]">{info?.name || d.id.replace(/_/g, " ")}</span><CharacterPill color={info?.color} /></span><span className="block text-[10px] text-[var(--text-muted)] tabular-nums">{t("You")} {d.your_pick_rate}% · {t("Community")}{" "}{d.community_pick_rate}% · {d.picked}/{d.offered} {t("offers")}</span></span><GapBadge gap={d.gap} /></Link>;
      };
      $[10] = bp;
      $[11] = cards;
      $[12] = lang;
      $[13] = t;
      $[14] = t2;
    } else {
      t2 = $[14];
    }
    t1 = rows.map(t2);
    $[4] = bp;
    $[5] = cards;
    $[6] = lang;
    $[7] = rows;
    $[8] = t;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[15] !== t1) {
    t2 = <div className="space-y-1">{t1}</div>;
    $[15] = t1;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  return t2;
}
function RelicDeltaList(t0) {
  const $ = _c(15);
  const {
    rows,
    relics
  } = t0;
  const t = useT();
  const bp = useBetaPrefix();
  if (rows.length === 0) {
    let t1;
    if ($[0] !== t) {
      t1 = t("Not enough data yet.");
      $[0] = t;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    let t2;
    if ($[2] !== t1) {
      t2 = <p className="text-xs text-[var(--text-muted)]">{t1}</p>;
      $[2] = t1;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t1;
  if ($[4] !== bp || $[5] !== relics || $[6] !== rows || $[7] !== t) {
    let t2;
    if ($[9] !== bp || $[10] !== relics || $[11] !== t) {
      t2 = d => {
        const info = relics[d.id];
        return <Link prefetch={false} key={d.id} href={`${bp}/relics/${d.id.toLowerCase()}`} className="flex items-center gap-3 py-1.5 hover:bg-[var(--bg-card-hover)] rounded px-2 -mx-2 transition-colors"><span className="flex-shrink-0 w-9 h-9 rounded bg-[var(--bg-primary)] border border-[var(--border-subtle)] overflow-hidden flex items-center justify-center">{info?.image_url ? <img src={imageUrl(info.image_url)} alt={info?.name || d.id} className="w-full h-full object-contain p-0.5" crossOrigin="anonymous" loading="lazy" /> : <span className="text-[9px] text-[var(--text-muted)]">—</span>}</span><span className="flex-1 min-w-0"><span className="flex items-center gap-1.5 min-w-0"><span className="truncate text-sm text-[var(--text-primary)]">{info?.name || d.id.replace(/_/g, " ")}</span><CharacterPill color={info?.pool} /></span><span className="block text-[10px] text-[var(--text-muted)] tabular-nums">{t("You")} {d.your_rate}% · {t("Community")} {d.community_rate}% · {d.runs_with}/{d.runs} {t("runs")}</span></span><GapBadge gap={d.gap} /></Link>;
      };
      $[9] = bp;
      $[10] = relics;
      $[11] = t;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t1 = rows.map(t2);
    $[4] = bp;
    $[5] = relics;
    $[6] = rows;
    $[7] = t;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  let t2;
  if ($[13] !== t1) {
    t2 = <div className="space-y-1">{t1}</div>;
    $[13] = t1;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}

// Weekly stacked run counts by mode, with the week's win rate as a line on
// the right axis. Weeks are the real cadence of play; months hid too much.
function ActivityChart(t0) {
  const $ = _c(51);
  const {
    rows
  } = t0;
  const t = useT();
  let t1;
  if ($[0] !== rows) {
    t1 = rows.map(_temp5);
    $[0] = rows;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const labels = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["solo", "coop", "daily", "custom"];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const modeKeys = t2 as const;
  let modeLabels;
  let t3;
  let t4;
  if ($[3] !== labels || $[4] !== rows || $[5] !== t) {
    modeLabels = {
      solo: t("Solo"),
      coop: t("Co-op"),
      daily: t("Daily"),
      custom: t("Custom")
    };
    t4 = labels;
    t3 = modeKeys.filter(k => rows.some(r_0 => r_0[k] > 0)).map(k_0 => ({
      type: "bar" as const,
      label: modeLabels[k_0],
      data: rows.map(r_1 => r_1[k_0]),
      backgroundColor: MODE_COLORS[k_0],
      stack: "runs",
      yAxisID: "y",
      borderRadius: 2
    }));
    $[3] = labels;
    $[4] = rows;
    $[5] = t;
    $[6] = modeLabels;
    $[7] = t3;
    $[8] = t4;
  } else {
    modeLabels = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("Win rate");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== rows) {
    t6 = rows.map(_temp6);
    $[11] = rows;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = {
      type: "line" as const,
      label: t5,
      data: t6,
      borderColor: "#e0ddd8",
      backgroundColor: "#e0ddd8",
      yAxisID: "y1",
      tension: 0.3,
      pointRadius: 2.5,
      borderWidth: 1.5
    };
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t7) {
    t8 = [...t3, t7];
    $[16] = t3;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t8) {
    t9 = {
      labels: t4,
      datasets: t8
    };
    $[19] = t4;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const data = t9;
  let t10;
  let t11;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      mode: "index",
      intersect: false
    };
    t11 = {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: _temp7
        }
      }
    };
    $[22] = t10;
    $[23] = t11;
  } else {
    t10 = $[22];
    t11 = $[23];
  }
  let t12;
  let t13;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      display: false
    };
    t13 = {
      display: false
    };
    $[24] = t12;
    $[25] = t13;
  } else {
    t12 = $[24];
    t13 = $[25];
  }
  let t14;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      stacked: true,
      grid: t12,
      border: t13,
      ticks: {
        color: TICK,
        font: {
          size: 9
        },
        maxRotation: 0,
        autoSkip: true
      }
    };
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      color: GRID
    };
    t16 = {
      display: false
    };
    $[27] = t15;
    $[28] = t16;
  } else {
    t15 = $[27];
    t16 = $[28];
  }
  let t17;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      stacked: true,
      position: "left",
      grid: t15,
      border: t16,
      ticks: {
        color: TICK,
        font: {
          size: 10
        },
        precision: 0
      }
    };
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = {
      display: false
    };
    t19 = {
      display: false
    };
    $[30] = t18;
    $[31] = t19;
  } else {
    t18 = $[30];
    t19 = $[31];
  }
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: t10,
      plugins: t11,
      scales: {
        x: t14,
        y: t17,
        y1: {
          position: "right",
          min: 0,
          max: 100,
          grid: t18,
          border: t19,
          ticks: {
            color: TICK,
            font: {
              size: 10
            },
            callback: _temp8
          }
        }
      }
    };
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  const opts = t20;
  let t21;
  if ($[33] !== data) {
    t21 = <div className="h-48"><Chart type="bar" data={data} options={opts} /></div>;
    $[33] = data;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== rows) {
    t22 = modeKeys.filter(k_1 => rows.some(r_3 => r_3[k_1] > 0));
    $[35] = rows;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== modeLabels || $[38] !== t22) {
    t23 = t22.map(k_2 => <span key={k_2} className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]"><span className="w-2.5 h-2.5 rounded-[3px]" style={{
        background: MODE_COLORS[k_2]
      }} />{modeLabels[k_2]}</span>);
    $[37] = modeLabels;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <span className="w-2.5 h-0.5" style={{
      background: "#e0ddd8"
    }} />;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t) {
    t25 = t("Win rate");
    $[41] = t;
    $[42] = t25;
  } else {
    t25 = $[42];
  }
  let t26;
  if ($[43] !== t25) {
    t26 = <span className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]">{t24}{t25}</span>;
    $[43] = t25;
    $[44] = t26;
  } else {
    t26 = $[44];
  }
  let t27;
  if ($[45] !== t23 || $[46] !== t26) {
    t27 = <div className="flex flex-wrap gap-3 mt-2">{t23}{t26}</div>;
    $[45] = t23;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== t21 || $[49] !== t27) {
    t28 = <div>{t21}{t27}</div>;
    $[48] = t21;
    $[49] = t27;
    $[50] = t28;
  } else {
    t28 = $[50];
  }
  return t28;
}

// Compact act x node-type table: your death rate per cell, community's under
// it. Red when you die there more than the community, green when less.
function _temp8(v) {
  return `${v}%`;
}
function _temp7(ctx) {
  return ctx.dataset.yAxisID === "y1" ? `${ctx.dataset.label}: ${ctx.parsed.y}%` : `${ctx.dataset.label}: ${ctx.parsed.y}`;
}
function _temp6(r_2) {
  return r_2.win_rate;
}
function _temp5(r) {
  return r.week.slice(5);
}
function DangerTable(t0) {
  const $ = _c(23);
  const {
    rows
  } = t0;
  const t = useT();
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== rows || $[1] !== t) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const acts = rows.filter(_temp10);
      if (acts.length === 0) {
        let t6;
        if ($[7] !== t) {
          t6 = t("Not enough data yet.");
          $[7] = t;
          $[8] = t6;
        } else {
          t6 = $[8];
        }
        let t7;
        if ($[9] !== t6) {
          t7 = <p className="text-xs text-[var(--text-muted)]">{t6}</p>;
          $[9] = t6;
          $[10] = t7;
        } else {
          t7 = $[10];
        }
        t5 = t7;
        break bb0;
      }
      const cols = DANGER_TYPES.filter(ty => acts.some(a_0 => (a_0.types?.[ty]?.visits || 0) >= 10));
      t4 = "overflow-x-auto";
      t2 = "w-full text-sm";
      let t6;
      if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <th className="text-left font-normal text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] pb-2" />;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      let t7;
      if ($[12] !== t) {
        t7 = ty_0 => <th key={ty_0} className="text-right font-normal text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] pb-2 pl-4">{t(DANGER_LABELS[ty_0])}</th>;
        $[12] = t;
        $[13] = t7;
      } else {
        t7 = $[13];
      }
      t3 = <thead><tr>{t6}{cols.map(t7)}</tr></thead>;
      t1 = acts.map(a_1 => <tr key={a_1.act} className="border-t border-[var(--border-subtle)]"><td className="py-1.5 text-[var(--text-secondary)]">{t("Act")} {a_1.act + 1}</td>{cols.map(ty_1 => {
          const cell = a_1.types?.[ty_1];
          if (!cell || (cell.visits || 0) < 10) {
            return <td key={ty_1} className="py-1.5 pl-4 text-right text-[var(--text-muted)]">—</td>;
          }
          const comm = cell.community_death_rate;
          const worse = comm != null && cell.death_rate > comm;
          const better = comm != null && cell.death_rate < comm;
          return <td key={ty_1} className="py-1.5 pl-4 text-right tabular-nums"><span className={worse ? "text-danger" : better ? "text-success" : "text-[var(--text-primary)]"}>{cell.death_rate}%</span>{comm != null && <span className="block text-[10px] text-[var(--text-muted)]">{comm}%</span>}</td>;
        })}</tr>);
    }
    $[0] = rows;
    $[1] = t;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t1 = $[2];
    t2 = $[3];
    t3 = $[4];
    t4 = $[5];
    t5 = $[6];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  let t6;
  if ($[14] !== t1) {
    t6 = <tbody>{t1}</tbody>;
    $[14] = t1;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t2 || $[17] !== t3 || $[18] !== t6) {
    t7 = <table className={t2}>{t3}{t6}</table>;
    $[16] = t2;
    $[17] = t3;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t4 || $[21] !== t7) {
    t8 = <div className={t4}>{t7}</div>;
    $[20] = t4;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}

// One 100%-stacked campfire distribution bar with per-segment colors.
function _temp10(a) {
  return Object.values(a.types || {}).some(_temp9);
}
function _temp9(c) {
  return (c.visits || 0) >= 10;
}
function RestStack(t0) {
  const $ = _c(8);
  const {
    rows,
    pctKey,
    dim
  } = t0;
  const t1 = `flex h-3 rounded-full overflow-hidden bg-[var(--bg-primary)] ${dim ? "opacity-50" : ""}`;
  let t2;
  if ($[0] !== pctKey || $[1] !== rows) {
    let t3;
    if ($[3] !== pctKey) {
      t3 = r => {
        const w = pctKey === "pct" ? r.pct : r.pct_low_hp;
        if (!w || w <= 0) {
          return null;
        }
        return <div key={r.id} style={{
          width: `${w}%`,
          background: REST_COLORS[r.id] || "#596068"
        }} title={`${r.label || r.id}: ${w}%`} />;
      };
      $[3] = pctKey;
      $[4] = t3;
    } else {
      t3 = $[4];
    }
    t2 = rows.map(t3);
    $[0] = pctKey;
    $[1] = rows;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function RestSection(t0) {
  const $ = _c(42);
  const {
    mine
  } = t0;
  const t = useT();
  let t1;
  if ($[0] !== mine) {
    t1 = mine.map(_temp11);
    $[0] = mine;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const commAll = t1;
  let t2;
  if ($[2] !== mine) {
    t2 = mine.some(_temp12);
    $[2] = mine;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasLowHp = t2;
  let t3;
  if ($[4] !== t) {
    t3 = t("All campfire visits");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t3) {
    t4 = <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">{t3}</p>;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t) {
    t5 = t("You");
    $[8] = t;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t5) {
    t6 = <span className="w-16 text-[10px] text-[var(--text-tertiary)]">{t5}</span>;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== mine) {
    t7 = <div className="flex-1"><RestStack rows={mine} pctKey="pct" /></div>;
    $[12] = mine;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <div className="flex items-center gap-2">{t6}{t7}</div>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t) {
    t9 = t("Community");
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <span className="w-16 text-[10px] text-[var(--text-tertiary)]">{t9}</span>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== commAll) {
    t11 = <div className="flex-1"><RestStack rows={commAll} pctKey="pct" dim={true} /></div>;
    $[21] = commAll;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <div className="flex items-center gap-2">{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t4 || $[28] !== t8) {
    t13 = <div className="space-y-1.5">{t4}{t8}{t12}</div>;
    $[26] = t12;
    $[27] = t4;
    $[28] = t8;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== hasLowHp || $[31] !== mine || $[32] !== t) {
    t14 = hasLowHp && <div className="space-y-1.5"><p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)]">{t("Arriving below half HP")}</p><div className="flex items-center gap-2"><span className="w-16 text-[10px] text-[var(--text-tertiary)]">{t("You")}</span><div className="flex-1"><RestStack rows={mine} pctKey="pct_low_hp" /></div></div><div className="flex items-center gap-2"><span className="w-16 text-[10px] text-[var(--text-tertiary)]">{t("Community")}</span><div className="flex-1"><RestStack rows={mine.map(_temp13)} pctKey="pct_low_hp" dim={true} /></div></div></div>;
    $[30] = hasLowHp;
    $[31] = mine;
    $[32] = t;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== mine) {
    t15 = mine.map(_temp14);
    $[34] = mine;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== t15) {
    t16 = <div className="flex flex-wrap gap-3">{t15}</div>;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t13 || $[39] !== t14 || $[40] !== t16) {
    t17 = <div className="space-y-4">{t13}{t14}{t16}</div>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t16;
    $[41] = t17;
  } else {
    t17 = $[41];
  }
  return t17;
}
function _temp14(r_2) {
  return <span key={r_2.id} className="flex items-center gap-1.5 text-[10px] text-[var(--text-muted)]"><span className="w-2.5 h-2.5 rounded-[3px]" style={{
      background: REST_COLORS[r_2.id] || "#596068"
    }} />{r_2.label || r_2.id} {r_2.pct}%<span className="text-[var(--text-tertiary)]">({r_2.community_pct ?? "\u2014"}%)</span></span>;
}
function _temp13(r_1) {
  return {
    ...r_1,
    pct_low_hp: r_1.community_pct_low_hp ?? 0
  };
}
function _temp12(r_0) {
  return (r_0.pct_low_hp ?? 0) > 0;
}
function _temp11(r) {
  return {
    ...r,
    pct: r.community_pct ?? 0
  };
}
function DeathColumn(t0) {
  const $ = _c(9);
  const {
    title,
    rows
  } = t0;
  if (rows.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== title) {
    t1 = <p className="text-[10px] uppercase tracking-wider text-[var(--text-tertiary)] mb-1.5">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== rows) {
    t2 = rows.map(_temp15);
    $[2] = rows;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <div className="space-y-1.5">{t2}</div>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <div>{t1}{t3}</div>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp15(d) {
  return <div key={d.id} className="flex items-center justify-between text-sm"><span className="text-[var(--text-primary)] truncate">{d.name || d.id.replace(/_/g, " ")}</span><span className="text-xs text-[var(--text-tertiary)] tabular-nums shrink-0 ml-2">{d.count} · {d.pct.toFixed(1)}%{d.community_pct != null && <span className="text-[var(--text-muted)]">{" "}({d.community_pct.toFixed(1)}%)</span>}</span></div>;
}
function displayCharacter(id: string): string {
  const bare = id.replace(/^CHARACTER\./, "").replace(/_/g, " ").toLowerCase();
  return bare.charAt(0).toUpperCase() + bare.slice(1);
}
function BestTile(t0) {
  const $ = _c(12);
  const {
    href,
    value,
    label,
    sub,
    rank
  } = t0;
  let t1;
  if ($[0] !== value) {
    t1 = <p className="text-lg font-bold text-[var(--text-primary)] tabular-nums">{value}</p>;
    $[0] = value;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label) {
    t2 = <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-0.5">{label}</p>;
    $[2] = label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== rank || $[5] !== sub) {
    t3 = (sub || rank) && <p className="text-[10px] text-[var(--text-tertiary)] mt-0.5">{sub}{rank?.rank ? <span className="text-[var(--accent-gold)]">{" "}#{rank.rank.toLocaleString()}<span className="text-[var(--text-muted)]">{" "}/ {rank.total.toLocaleString()}</span></span> : null}</p>;
    $[4] = rank;
    $[5] = sub;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== href || $[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <Link prefetch={false} href={href} className="bg-[var(--bg-primary)] rounded-lg p-3 text-center hover:bg-[var(--bg-card-hover)] transition-colors">{t1}{t2}{t3}</Link>;
    $[7] = href;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}

// The full merged overview+insights layout, shared between the signed-in
// profile tab and the public /players page. `bests` and `personalRanks` are
// self-only extras; the public page simply omits them. Other players' daily
// runs deliberately stay off profiles (the leaderboards page has that board)
// — a stranger's run on your own profile just reads as a bug.
export function InsightsPanels(t0) {
  const $ = _c(160);
  const {
    data,
    cards,
    relics,
    lang,
    bests,
    personalRanks
  } = t0;
  const t = useT();
  const bp = useBetaPrefix();
  let bosses;
  let elites;
  let t1;
  if ($[0] !== data.deaths?.encounters) {
    const deathsEnc = data.deaths?.encounters || [];
    bosses = deathsEnc.filter(_temp16).slice(0, 5);
    elites = deathsEnc.filter(_temp17).slice(0, 5);
    t1 = deathsEnc.filter(_temp18).slice(0, 5);
    $[0] = data.deaths?.encounters;
    $[1] = bosses;
    $[2] = elites;
    $[3] = t1;
  } else {
    bosses = $[1];
    elites = $[2];
    t1 = $[3];
  }
  const fights = t1;
  let t2;
  if ($[4] !== data.rest_sites) {
    t2 = data.rest_sites || [];
    $[4] = data.rest_sites;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const restSites = t2;
  let t3;
  if ($[6] !== bests || $[7] !== bosses || $[8] !== bp || $[9] !== cards || $[10] !== data.activity || $[11] !== data.ancient_picks || $[12] !== data.ascension_matrix || $[13] !== data.by_character || $[14] !== data.card_picks || $[15] !== data.elo || $[16] !== data.event_divergence || $[17] !== data.map_danger || $[18] !== data.percentiles || $[19] !== data.records || $[20] !== data.relic_picks || $[21] !== data.streaks || $[22] !== data.total_losses || $[23] !== data.total_runs || $[24] !== data.total_wins || $[25] !== data.win_rate || $[26] !== elites || $[27] !== fights || $[28] !== lang || $[29] !== personalRanks?.fastest_daily || $[30] !== personalRanks?.fastest_multi || $[31] !== personalRanks?.fastest_solo || $[32] !== personalRanks?.highest_ascension || $[33] !== relics || $[34] !== restSites || $[35] !== t) {
    const boons = (data.ancient_picks || []).filter(_temp19).sort(_temp20).slice(0, 8);
    let t4;
    if ($[37] !== data.event_divergence) {
      t4 = data.event_divergence || [];
      $[37] = data.event_divergence;
      $[38] = t4;
    } else {
      t4 = $[38];
    }
    const divergence = t4;
    let t5;
    if ($[39] !== data.records) {
      t5 = data.records || {};
      $[39] = data.records;
      $[40] = t5;
    } else {
      t5 = $[40];
    }
    const records = t5;
    let t6;
    if ($[41] !== data.activity) {
      t6 = data.activity || [];
      $[41] = data.activity;
      $[42] = t6;
    } else {
      t6 = $[42];
    }
    let t7;
    if ($[43] !== t6) {
      t7 = t6.filter(_temp21);
      $[43] = t6;
      $[44] = t7;
    } else {
      t7 = $[44];
    }
    const activity = t7;
    const pct = data.percentiles;
    const streaks = data.streaks;
    const elo = data.elo;
    const hasRecords = records.fastest_win || records.longest_run || records.biggest_deck;
    let t8;
    if ($[45] !== bests) {
      t8 = bests && Object.values(bests).some(Boolean);
      $[45] = bests;
      $[46] = t8;
    } else {
      t8 = $[46];
    }
    const hasBests = t8;
    let t10;
    let t11;
    let t12;
    let t13;
    let t9;
    if ($[47] !== bests || $[48] !== bp || $[49] !== data.by_character || $[50] !== data.total_losses || $[51] !== data.total_runs || $[52] !== data.total_wins || $[53] !== data.win_rate || $[54] !== elo || $[55] !== hasBests || $[56] !== hasRecords || $[57] !== lang || $[58] !== pct || $[59] !== personalRanks?.fastest_daily || $[60] !== personalRanks?.fastest_multi || $[61] !== personalRanks?.fastest_solo || $[62] !== personalRanks?.highest_ascension || $[63] !== records.biggest_deck || $[64] !== records.fastest_win || $[65] !== records.longest_run || $[66] !== streaks || $[67] !== t) {
      const characters = (data.by_character || []).filter(_temp22).sort(_temp23);
      const losses = data.total_losses ?? (data.total_wins != null ? data.total_runs - data.total_wins : null);
      t9 = "space-y-4";
      let t14;
      if ($[73] !== t) {
        t14 = t("Runs");
        $[73] = t;
        $[74] = t14;
      } else {
        t14 = $[74];
      }
      let t15;
      if ($[75] !== data.total_runs || $[76] !== t14) {
        t15 = [t14, data.total_runs];
        $[75] = data.total_runs;
        $[76] = t14;
        $[77] = t15;
      } else {
        t15 = $[77];
      }
      let t16;
      if ($[78] !== t) {
        t16 = t("Wins");
        $[78] = t;
        $[79] = t16;
      } else {
        t16 = $[79];
      }
      const t17 = data.total_wins ?? 0;
      let t18;
      if ($[80] !== t16 || $[81] !== t17) {
        t18 = [t16, t17];
        $[80] = t16;
        $[81] = t17;
        $[82] = t18;
      } else {
        t18 = $[82];
      }
      let t19;
      if ($[83] !== t) {
        t19 = t("Losses");
        $[83] = t;
        $[84] = t19;
      } else {
        t19 = $[84];
      }
      const t20 = losses ?? 0;
      let t21;
      if ($[85] !== t19 || $[86] !== t20) {
        t21 = [t19, t20];
        $[85] = t19;
        $[86] = t20;
        $[87] = t21;
      } else {
        t21 = $[87];
      }
      let t22;
      if ($[88] !== t) {
        t22 = t("Win rate");
        $[88] = t;
        $[89] = t22;
      } else {
        t22 = $[89];
      }
      const t23 = `${data.win_rate ?? 0}%`;
      let t24;
      if ($[90] !== t22 || $[91] !== t23) {
        t24 = [t22, t23];
        $[90] = t22;
        $[91] = t23;
        $[92] = t24;
      } else {
        t24 = $[92];
      }
      if ($[93] !== t15 || $[94] !== t18 || $[95] !== t21 || $[96] !== t24) {
        t10 = <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">{[t15, t18, t21, t24].map(_temp24)}</div>;
        $[93] = t15;
        $[94] = t18;
        $[95] = t21;
        $[96] = t24;
        $[97] = t10;
      } else {
        t10 = $[97];
      }
      if ($[98] !== elo || $[99] !== lang || $[100] !== pct || $[101] !== streaks || $[102] !== t) {
        t11 = (pct || streaks || elo) && <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{pct && <Section title={t("How you rank")}><div className="space-y-4"><PercentileSlider label={t("Win rate")} valueText={`${pct.win_rate}%`} percentile={pct.win_rate_percentile} lang={lang} /><PercentileSlider label={t("Runs submitted")} valueText={`${pct.runs}`} percentile={pct.runs_percentile} lang={lang} /></div></Section>}{streaks && <Section title={t("Streaks")}><div className="grid grid-cols-2 gap-3"><div className="bg-[var(--bg-primary)] rounded-lg p-4 text-center"><p className="text-2xl font-bold text-[var(--accent-gold)] tabular-nums">{streaks.current_win_streak}</p><p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1">{t("Current win streak")}</p></div><div className="bg-[var(--bg-primary)] rounded-lg p-4 text-center"><p className="text-2xl font-bold text-[var(--accent-gold)] tabular-nums">{streaks.best_win_streak}</p><p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1">{t("Best win streak")}</p></div></div></Section>}{elo && <Section title={t("Elo")}><div className="grid grid-cols-2 gap-3"><div className="bg-[var(--bg-primary)] rounded-lg p-4 text-center"><p className="text-2xl font-bold text-[var(--accent-gold)] tabular-nums">{Math.round(elo.current)}</p><p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1">{t("Current Elo")}</p></div><div className="bg-[var(--bg-primary)] rounded-lg p-4 text-center"><p className="text-2xl font-bold text-[var(--accent-gold)] tabular-nums">{Math.round(elo.peak)}</p><p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1">{t("Highest Elo Score Achieved")}</p></div></div>{elo.by_character && Object.keys(elo.by_character).length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3">{Object.entries(elo.by_character).sort(_temp25).map(t25 => {
                const [id, bc] = t25;
                return <div key={id} className="bg-[var(--bg-primary)] rounded-lg px-3 py-2 flex items-center justify-between gap-2 text-xs"><CharacterTag id={id} size={16} /><span className="text-right shrink-0"><span className="text-sm font-semibold tabular-nums text-[var(--text-primary)]">{Math.round(bc.elo)}</span><span className="text-[10px] text-[var(--text-muted)] ml-1.5">{bc.runs.toLocaleString()} {t("Runs")}</span></span></div>;
              })}</div>}</Section>}</div>;
        $[98] = elo;
        $[99] = lang;
        $[100] = pct;
        $[101] = streaks;
        $[102] = t;
        $[103] = t11;
      } else {
        t11 = $[103];
      }
      if ($[104] !== bests || $[105] !== bp || $[106] !== hasBests || $[107] !== hasRecords || $[108] !== personalRanks?.fastest_daily || $[109] !== personalRanks?.fastest_multi || $[110] !== personalRanks?.fastest_solo || $[111] !== personalRanks?.highest_ascension || $[112] !== records.biggest_deck || $[113] !== records.fastest_win || $[114] !== records.longest_run || $[115] !== t) {
        t12 = (hasRecords || hasBests) && <Section title={t("Personal Bests")}><div className="grid grid-cols-2 sm:grid-cols-3 gap-3">{bests?.fastest_solo && <BestTile href={`${bp}/runs/${bests.fastest_solo.run_hash}`} value={formatTime(bests.fastest_solo.run_time)} label={t("Fastest Solo")} sub={`${displayCharacter(bests.fastest_solo.character)} A${bests.fastest_solo.ascension}`} rank={personalRanks?.fastest_solo} />}{bests?.fastest_multi && <BestTile href={`${bp}/runs/${bests.fastest_multi.run_hash}`} value={formatTime(bests.fastest_multi.run_time)} label={t("Fastest Co-op")} sub={`${displayCharacter(bests.fastest_multi.character)} A${bests.fastest_multi.ascension}`} rank={personalRanks?.fastest_multi} />}{bests?.highest_ascension && <BestTile href={`${bp}/runs/${bests.highest_ascension.run_hash}`} value={`A${bests.highest_ascension.ascension}`} label={t("Highest Ascension")} sub={displayCharacter(bests.highest_ascension.character)} rank={personalRanks?.highest_ascension} />}{bests?.fastest_daily && <BestTile href={`${bp}/runs/${bests.fastest_daily.run_hash}`} value={formatTime(bests.fastest_daily.run_time)} label={t("Fastest Daily (All Time)")} sub={displayCharacter(bests.fastest_daily.character)} rank={personalRanks?.fastest_daily} />}{!bests?.fastest_solo && records.fastest_win && <BestTile href={`${bp}/runs/${records.fastest_win.run_hash}`} value={formatTime(records.fastest_win.run_time)} label={t("Fastest win")} />}{records.longest_run && <BestTile href={`${bp}/runs/${records.longest_run.run_hash}`} value={formatTime(records.longest_run.run_time)} label={t("Longest run")} />}{records.biggest_deck && <BestTile href={`${bp}/runs/${records.biggest_deck.run_hash}`} value={`${records.biggest_deck.size} ${t("cards")}`} label={t("Biggest deck")} />}</div></Section>;
        $[104] = bests;
        $[105] = bp;
        $[106] = hasBests;
        $[107] = hasRecords;
        $[108] = personalRanks?.fastest_daily;
        $[109] = personalRanks?.fastest_multi;
        $[110] = personalRanks?.fastest_solo;
        $[111] = personalRanks?.highest_ascension;
        $[112] = records.biggest_deck;
        $[113] = records.fastest_win;
        $[114] = records.longest_run;
        $[115] = t;
        $[116] = t12;
      } else {
        t12 = $[116];
      }
      t13 = characters.length > 0 && <Section title={t("Characters")}><div className="space-y-3">{characters.map(c_0 => {
            const hex = CHARACTER_HEX[c_0.id.toLowerCase()] || "var(--text-muted)";
            return <div key={c_0.id} className="space-y-1"><div className="flex items-center justify-between text-sm"><span className="font-medium" style={{
                  color: textColor(hex)
                }}>{(c_0.name || displayCharacter(c_0.id)).replace(/^The\s+/i, "")}</span><span className="text-xs text-[var(--text-tertiary)] tabular-nums">{c_0.runs} {t("runs")} · {c_0.share}%</span></div><div className="space-y-0.5"><div className="flex items-center gap-2"><span className="w-16 text-[10px] text-[var(--text-tertiary)]">{t("You")}</span><div className="flex-1 h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden"><div className="h-full rounded-full" style={{
                      width: `${Math.min(c_0.win_rate, 100)}%`,
                      backgroundColor: hex
                    }} /></div><span className="w-12 text-right text-[10px] tabular-nums text-[var(--text-primary)]">{c_0.win_rate}%</span></div><div className="flex items-center gap-2"><span className="w-16 text-[10px] text-[var(--text-tertiary)]">{t("Community")}</span><div className="flex-1 h-1.5 rounded-full bg-[var(--bg-primary)] overflow-hidden"><div className="h-full rounded-full opacity-40" style={{
                      width: `${Math.min(c_0.community_win_rate ?? 0, 100)}%`,
                      backgroundColor: hex
                    }} /></div><span className="w-12 text-right text-[10px] tabular-nums text-[var(--text-tertiary)]">{c_0.community_win_rate != null ? `${c_0.community_win_rate}%` : "\u2014"}</span></div></div></div>;
          })}</div></Section>;
      $[47] = bests;
      $[48] = bp;
      $[49] = data.by_character;
      $[50] = data.total_losses;
      $[51] = data.total_runs;
      $[52] = data.total_wins;
      $[53] = data.win_rate;
      $[54] = elo;
      $[55] = hasBests;
      $[56] = hasRecords;
      $[57] = lang;
      $[58] = pct;
      $[59] = personalRanks?.fastest_daily;
      $[60] = personalRanks?.fastest_multi;
      $[61] = personalRanks?.fastest_solo;
      $[62] = personalRanks?.highest_ascension;
      $[63] = records.biggest_deck;
      $[64] = records.fastest_win;
      $[65] = records.longest_run;
      $[66] = streaks;
      $[67] = t;
      $[68] = t10;
      $[69] = t11;
      $[70] = t12;
      $[71] = t13;
      $[72] = t9;
    } else {
      t10 = $[68];
      t11 = $[69];
      t12 = $[70];
      t13 = $[71];
      t9 = $[72];
    }
    let t14;
    if ($[117] !== activity || $[118] !== lang || $[119] !== t) {
      t14 = activity.length >= 2 && <Section title={t("Weekly activity")}><ActivityChart rows={activity} lang={lang} /></Section>;
      $[117] = activity;
      $[118] = lang;
      $[119] = t;
      $[120] = t14;
    } else {
      t14 = $[120];
    }
    let t15;
    if ($[121] !== elo || $[122] !== t) {
      t15 = (elo?.history?.length ?? 0) >= 2 && <Section title={t("Elo over time")}><EloTrajectory points={elo.history} runLabel={t("Run")} winLabel={t("win")} lossLabel={t("loss")} axisLabel={t("rated solo A10 run")} /></Section>;
      $[121] = elo;
      $[122] = t;
      $[123] = t15;
    } else {
      t15 = $[123];
    }
    let t16;
    if ($[124] !== data.ascension_matrix || $[125] !== lang || $[126] !== t) {
      t16 = Object.keys(data.ascension_matrix || {}).length > 0 && <Section title={t("Win rate by character and ascension")}><AscensionHeatmap matrix={data.ascension_matrix} lang={lang} /></Section>;
      $[124] = data.ascension_matrix;
      $[125] = lang;
      $[126] = t;
      $[127] = t16;
    } else {
      t16 = $[127];
    }
    let t17;
    if ($[128] !== bosses || $[129] !== elites || $[130] !== fights || $[131] !== lang || $[132] !== t) {
      t17 = (bosses.length > 0 || elites.length > 0 || fights.length > 0) && <Section title={t("What kills you")}><p className="text-[11px] text-[var(--text-muted)] mb-3">{t("Share of your deaths. Gray = community share.")}</p><div className="grid grid-cols-1 md:grid-cols-3 gap-4"><DeathColumn title={t("Bosses")} rows={bosses} lang={lang} /><DeathColumn title={t("Elites")} rows={elites} lang={lang} /><DeathColumn title={t("Monsters")} rows={fights} lang={lang} /></div></Section>;
      $[128] = bosses;
      $[129] = elites;
      $[130] = fights;
      $[131] = lang;
      $[132] = t;
      $[133] = t17;
    } else {
      t17 = $[133];
    }
    let t18;
    if ($[134] !== data.map_danger || $[135] !== lang || $[136] !== t) {
      t18 = (data.map_danger || []).length > 0 && <Section title={t("Where you die")}><p className="text-[11px] text-[var(--text-muted)] mb-3">{t("How often a visit kills you. Gray = community.")}</p><DangerTable rows={data.map_danger} lang={lang} /></Section>;
      $[134] = data.map_danger;
      $[135] = lang;
      $[136] = t;
      $[137] = t18;
    } else {
      t18 = $[137];
    }
    let t19;
    if ($[138] !== lang || $[139] !== restSites || $[140] !== t) {
      t19 = restSites.length > 0 && <Section title={t("Your campfire choices")}><RestSection mine={restSites} lang={lang} /></Section>;
      $[138] = lang;
      $[139] = restSites;
      $[140] = t;
      $[141] = t19;
    } else {
      t19 = $[141];
    }
    let t20;
    if ($[142] !== t18 || $[143] !== t19) {
      t20 = <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{t18}{t19}</div>;
      $[142] = t18;
      $[143] = t19;
      $[144] = t20;
    } else {
      t20 = $[144];
    }
    let t21;
    if ($[145] !== cards || $[146] !== data.card_picks || $[147] !== lang || $[148] !== t) {
      t21 = data.card_picks && <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><Section title={t("Cards you take more than most")}><CardDeltaList rows={data.card_picks.over_picked || []} cards={cards} lang={lang} /></Section><Section title={t("Cards you take less than most")}><CardDeltaList rows={data.card_picks.under_picked || []} cards={cards} lang={lang} /></Section></div>;
      $[145] = cards;
      $[146] = data.card_picks;
      $[147] = lang;
      $[148] = t;
      $[149] = t21;
    } else {
      t21 = $[149];
    }
    let t22;
    if ($[150] !== data.relic_picks || $[151] !== lang || $[152] !== relics || $[153] !== t) {
      t22 = data.relic_picks && (data.relic_picks.over_carried.length > 0 || data.relic_picks.under_carried.length > 0) && <div className="grid grid-cols-1 md:grid-cols-2 gap-4"><Section title={t("Relics you take more than most")}><RelicDeltaList rows={data.relic_picks.over_carried || []} relics={relics} lang={lang} /></Section><Section title={t("Relics you take less than most")}><RelicDeltaList rows={data.relic_picks.under_carried || []} relics={relics} lang={lang} /></Section></div>;
      $[150] = data.relic_picks;
      $[151] = lang;
      $[152] = relics;
      $[153] = t;
      $[154] = t22;
    } else {
      t22 = $[154];
    }
    let t23;
    if ($[155] !== bp || $[156] !== divergence || $[157] !== lang || $[158] !== t) {
      t23 = divergence.length > 0 && <Section title={t("Event choices where you differ")}><div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">{divergence.map(d_2 => <div key={`${d_2.event_id}-${d_2.option_id}`} className="space-y-1"><div className="flex items-center justify-between text-sm gap-2"><span className="min-w-0 truncate"><Link prefetch={false} href={`${bp}/events/${d_2.event_id.toLowerCase()}`} className="text-[var(--text-primary)] hover:text-[var(--accent-gold)] transition-colors">{d_2.event_name || d_2.event_id.replace(/_/g, " ")}</Link><span className="text-[var(--text-muted)]">{" "}· {d_2.option_label || d_2.option_id.replace(/_/g, " ")}</span></span><GapBadge gap={d_2.gap} /></div><CompareBars you={d_2.your_pct} community={d_2.community_pct} lang={lang} /></div>)}</div></Section>;
      $[155] = bp;
      $[156] = divergence;
      $[157] = lang;
      $[158] = t;
      $[159] = t23;
    } else {
      t23 = $[159];
    }
    t3 = <div className={t9}>{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}{t20}{t21}{t22}{t23}{boons.length > 0 && <Section title={t("Your boon take rates")}><div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">{boons.map(b_3 => <div key={b_3.id} className="space-y-1"><div className="flex items-center justify-between text-sm"><span className="text-[var(--text-primary)]">{b_3.name || b_3.id.replace(/_/g, " ")}</span><span className="text-xs text-[var(--text-tertiary)] tabular-nums">{b_3.count}/{b_3.offered} {t("offers")}</span></div><CompareBars you={b_3.take_rate} community={b_3.community_take_rate} lang={lang} /></div>)}</div></Section>}</div>;
    $[6] = bests;
    $[7] = bosses;
    $[8] = bp;
    $[9] = cards;
    $[10] = data.activity;
    $[11] = data.ancient_picks;
    $[12] = data.ascension_matrix;
    $[13] = data.by_character;
    $[14] = data.card_picks;
    $[15] = data.elo;
    $[16] = data.event_divergence;
    $[17] = data.map_danger;
    $[18] = data.percentiles;
    $[19] = data.records;
    $[20] = data.relic_picks;
    $[21] = data.streaks;
    $[22] = data.total_losses;
    $[23] = data.total_runs;
    $[24] = data.total_wins;
    $[25] = data.win_rate;
    $[26] = elites;
    $[27] = fights;
    $[28] = lang;
    $[29] = personalRanks?.fastest_daily;
    $[30] = personalRanks?.fastest_multi;
    $[31] = personalRanks?.fastest_solo;
    $[32] = personalRanks?.highest_ascension;
    $[33] = relics;
    $[34] = restSites;
    $[35] = t;
    $[36] = t3;
  } else {
    t3 = $[36];
  }
  return t3;
}

// The map is keyed by the request it came from, so a language switch shows
// nothing (rather than the previous language's names) until the new catalog
// has arrived, and a failed request leaves the map empty instead of stale.
function _temp25(t0, t1) {
  const [a_1] = t0;
  const [b_2] = t1;
  return CHAR_ORDER.indexOf(a_1.toUpperCase()) - CHAR_ORDER.indexOf(b_2.toUpperCase());
}
function _temp24(t0) {
  const [label, value] = t0;
  return <div key={String(label)} className="bg-[var(--bg-card)] rounded-lg border border-[var(--border-subtle)] p-4 text-center"><p className="text-2xl font-bold text-[var(--accent-gold)] tabular-nums">{value}</p><p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mt-1">{label}</p></div>;
}
function _temp23(a_0, b_1) {
  return CHAR_ORDER.indexOf(a_0.id.toUpperCase()) - CHAR_ORDER.indexOf(b_1.id.toUpperCase());
}
function _temp22(c) {
  return c.runs > 0;
}
function _temp21(r) {
  return r.runs > 0;
}
function _temp20(a, b_0) {
  return Math.abs(b_0.take_rate - (b_0.community_take_rate ?? b_0.take_rate)) - Math.abs(a.take_rate - (a.community_take_rate ?? a.take_rate));
}
function _temp19(b) {
  return (b.offered ?? 0) >= 5 && b.take_rate != null;
}
function _temp18(d_1) {
  return !d_1.id.endsWith("_BOSS") && !d_1.id.endsWith("_ELITE");
}
function _temp17(d_0) {
  return d_0.id.endsWith("_ELITE");
}
function _temp16(d) {
  return d.id.endsWith("_BOSS");
}
function useEntityMap(path) {
  const $ = _c(4);
  const lang = useGameLocale();
  const url = `${API}${path}${path.includes("?") ? "&" : "?"}lang=${encodeURIComponent(lang)}`;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      url: "",
      map: {}
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [state, setState] = useState(t0);
  let t1;
  let t2;
  if ($[1] !== url) {
    t1 = () => {
      let alive = true;
      cachedFetch(url).then(rows => {
        if (!alive) {
          return;
        }
        const m = {};
        for (const c of Array.isArray(rows) ? rows : []) {
          m[c.id.toUpperCase()] = c;
        }
        setState({
          url,
          map: m
        });
      }).catch(() => {
        if (alive) {
          setState({
            url,
            map: {}
          });
        }
      });
      return () => {
        alive = false;
      };
    };
    t2 = [url];
    $[1] = url;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  return state.url === url ? state.map : EMPTY_ENTITY_MAP;
}
const EMPTY_ENTITY_MAP: Record<string, EntityInfo> = {};
export function useCardMap() {
  return useEntityMap("/api/cards");
}
export function useRelicMap() {
  return useEntityMap("/api/relics");
}
export default function ProfileInsights(t0) {
  const $ = _c(44);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    bests,
    personalRanks
  } = t1;
  const lang = useGameLocale();
  const t = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [claimedRuns, setClaimedRuns] = useState(null);
  const [filters, setFilters] = useState(EMPTY_INSIGHT_FILTERS);
  const filtered = !!filters.character || !!filters.ascension || !!filters.players || !!filters.version;
  const cards = useCardMap();
  const relics = useRelicMap();
  let t2;
  let t3;
  if ($[2] !== filters) {
    t2 = () => {
      let alive = true;
      let timer;
      setLoading(true);
      const q = insightFilterQuery(filters);
      const load = () => {
        fetch(`${API}/api/auth/insights${q}`, {
          credentials: "include"
        }).then(_temp26).then(d => {
          if (!alive) {
            return;
          }
          if (d && d.building) {
            setBuilding(true);
            if (typeof d.claimed_runs === "number") {
              setClaimedRuns(d.claimed_runs);
            }
            timer = setTimeout(load, 5000);
            return;
          }
          setBuilding(false);
          setData(d);
          setLoading(false);
        }).catch(() => {
          if (!alive) {
            return;
          }
          setBuilding(false);
          setLoading(false);
        });
      };
      load();
      return () => {
        alive = false;
        if (timer) {
          clearTimeout(timer);
        }
      };
    };
    t3 = [filters];
    $[2] = filters;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  if (!data && (building || loading)) {
    let t4;
    if ($[5] !== building || $[6] !== claimedRuns || $[7] !== t) {
      t4 = building && <p className="text-sm text-[var(--text-secondary)]">{t("Crunching your runs. The first load can take a minute or two.")}{claimedRuns ? ` · ${claimedRuns.toLocaleString()} ${t("runs")}` : ""}</p>;
      $[5] = building;
      $[6] = claimedRuns;
      $[7] = t;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    let t5;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = [...Array(4)].map(_temp27);
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    let t6;
    if ($[10] !== t4 || $[11] !== t5) {
      t6 = <div className="space-y-3">{t4}{t5}</div>;
      $[10] = t4;
      $[11] = t5;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    return t6;
  }
  if (!filtered && (!data || !data.runs_walked)) {
    let t4;
    if ($[13] !== t) {
      t4 = t("No insights yet. Upload and claim runs to see how you play.");
      $[13] = t;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    let t5;
    if ($[15] !== t4) {
      t5 = <p className="text-sm text-[var(--text-secondary)] py-4">{t4}</p>;
      $[15] = t4;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    return t5;
  }
  const t4 = `space-y-4 ${loading ? "opacity-60" : ""}`;
  let t5;
  if ($[17] !== t) {
    t5 = t("Your runs through the community lens. Every section compares you with all submitted runs.");
    $[17] = t;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== data?.runs_capped || $[20] !== t) {
    t6 = data?.runs_capped ? ` ${t("Based on your most recent runs only.")}` : "";
    $[19] = data?.runs_capped;
    $[20] = t;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t5 || $[23] !== t6) {
    t7 = <p className="text-xs text-[var(--text-muted)]">{t5}{t6}</p>;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== filters || $[26] !== lang) {
    t8 = <InsightsFilterBar value={filters} onChange={setFilters} lang={lang} />;
    $[25] = filters;
    $[26] = lang;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t7 || $[29] !== t8) {
    t9 = <div className="flex flex-wrap items-center justify-between gap-3">{t7}{t8}</div>;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== bests || $[32] !== cards || $[33] !== data || $[34] !== filtered || $[35] !== lang || $[36] !== personalRanks || $[37] !== relics || $[38] !== t) {
    t10 = data && data.runs_walked ? <InsightsPanels data={data} cards={cards} relics={relics} lang={lang} bests={filtered ? undefined : bests} personalRanks={filtered ? undefined : personalRanks} /> : <p className="text-sm text-[var(--text-secondary)] py-4">{t("Not enough data yet.")}</p>;
    $[31] = bests;
    $[32] = cards;
    $[33] = data;
    $[34] = filtered;
    $[35] = lang;
    $[36] = personalRanks;
    $[37] = relics;
    $[38] = t;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t4 || $[42] !== t9) {
    t11 = <div className={t4}>{t9}{t10}</div>;
    $[40] = t10;
    $[41] = t4;
    $[42] = t9;
    $[43] = t11;
  } else {
    t11 = $[43];
  }
  return t11;
}
function _temp27(_, i) {
  return <div key={i} className="h-24 bg-[var(--bg-card)] rounded-lg animate-pulse" />;
}
function _temp26(r) {
  return r.ok ? r.json() : null;
}